import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo } from "react";

import CustomCard from "components/CustomCard";
import { HELIXTAP_INTRODUCER_ID } from "components/Helixtap/constants";
import HelixtapFobDailyWidget from "components/Helixtap/HelixtapPriceAssessmentsWidget/FobDaily";
import PaneLayout from "components/PaneLayout";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useEffectOnce } from "hooks/useEffectOnce";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

import styles from "./style.module.scss";
import useInterfaceSetting from "./useInterfaceSetting";

const SignUpForm = dynamic(() => import("components/SignUpForm"));
const DashboardPageMobile = dynamic(() => import("./DashboardPageMobile"));
const DashboardPageTabs = dynamic(() => import("./DashboardPageTabs"));
const DashboardPageTopPicks = dynamic(() => import("./DashboardPageTopPicks"));
const DashboardPageMyFeed = dynamic(() => import("./DashboardPageMyFeed"));
const DashboardPageAllInsights = dynamic(
  () => import("./DashboardPageAllInsights")
);
const DashboardPageFollowed = dynamic(() => import("./DashboardPageFollowed"));
const DashboardPageMyActivity = dynamic(
  () => import("./DashboardPageMyActivity")
);
const DashboardPageWorkspace = dynamic(
  () => import("./DashboardPageWorkspace")
);
const DashboardPageWidget = dynamic(() => import("./DashboardPageWidget"));
const DianomiAd = dynamic(() => import("components/DianomiAd"), {
  ssr: false,
});

type DashboardPageProps = {};

const DashboardPage = ({}: DashboardPageProps) => {
  const media = useMediaQuery();

  const router = useRouter();
  const collection = router.query?.collection as string;

  const { authenticated, account } = useAuthentication();

  useInterfaceSetting();

  const content = useMemo(() => {
    if (["authors-picks", "editors-picks", "trending"].includes(collection)) {
      return <DashboardPageTopPicks />;
    }
    if (collection === "my-reading-list") {
      return <DashboardPageMyFeed />;
    }
    if (collection === "all-insights") {
      return <DashboardPageAllInsights />;
    }
    if (
      [
        "from-ip-i-follow",
        "followed-collections",
        "from-all-followed",
      ].includes(collection)
    ) {
      return <DashboardPageFollowed />;
    }
    if (["recently-read", "bookmarked", "liked"].includes(collection)) {
      return <DashboardPageMyActivity />;
    }
    if (["workspace"].includes(collection)) {
      return <DashboardPageWorkspace />;
    }
    return <DashboardPageAllInsights />;
  }, [collection]);

  const hideRightPane = useMemo(
    () => ["workspace"].includes(collection),
    [collection]
  );

  const { setShowBackButton } = useContext(TopNavBarContext);
  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  const { trackActivity } = useActivityTracker();

  useEffectOnce(
    (ack) => {
      if (account) {
        ack();
        trackActivity("dashboard.view", account, { collection });
      }
    },
    [account, collection, trackActivity]
  );

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);
  const title = "Dashboard | Smartkarma";
  const description =
    "Smartkarma is the Independent Investment Research Network, uniting Insight Providers, Investors, and Investor Relations Professionals.";

  const metaTags = useMemo(() => {
    const defaultMetatags = (
      <>
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://assets.smartkarma.com/v3/img/smartkarma-research-reinvented-dark.png"
        />
        <meta name="robots" content="index, follow" />
      </>
    );

    if (["all-insights", "trending"].includes(collection)) {
      return defaultMetatags;
    }

    return (
      <>
        {defaultMetatags}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </>
    );
  }, [canonicalUrl, collection]);

  if (media.mobile) {
    return (
      <>
        <Head>{metaTags}</Head>
        <div className={styles.mobileLayout}>
          <DashboardPageMobile>{content}</DashboardPageMobile>
        </div>
      </>
    );
  }

  return (
    <>
      <Head>{metaTags}</Head>
      <PaneLayout
        className={styles.layout}
        hideActionBar={hideRightPane}
        hideRight={hideRightPane}
        left={<DashboardPageTabs />}
        right={
          <div className="mt-[60px]">
            {account?.introducer?.id === HELIXTAP_INTRODUCER_ID && (
              <CustomCard className="flex-1 p-[15px]">
                <HelixtapFobDailyWidget />
              </CustomCard>
            )}
            <DianomiAd type="right_sidebar" />
            {authenticated ? (
              <DashboardPageWidget />
            ) : (
              <CustomCard className={styles.card}>
                <SignUpForm type="compact" />
              </CustomCard>
            )}
          </div>
        }
      >
        <div className={styles.content}>{content}</div>
      </PaneLayout>
    </>
  );
};

export default DashboardPage;
